import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

export const THEMES = [
  {
    id: 'ocean',
    name: 'Ocean',
    description: 'Klinik dr. Jo — hijau muda subtle',
    colors: {
      primary: '#22c55e',
      primaryDark: '#16a34a',
      primaryLight: '#4ade80',
    },
    // Tailwind-compatible gradient classes per theme
    classes: {
      header: 'from-green-400 via-green-300 to-green-200',
      headerHover: 'hover:bg-white/10',
      navActive: 'from-green-500 to-green-600',
      btnPrimary: 'from-green-500 to-green-600 hover:from-green-600 hover:to-green-700',
      btnPrimaryShadow: 'shadow-green-500/25',
      focusRing: 'focus:ring-green-400',
      loginBg: 'from-green-100 via-white to-green-50',
      spinner: 'text-green-500',
      accent: 'text-green-600',
      accentBg: 'bg-green-50',
      accentBorder: 'border-green-200',
    },
    swatch: '#22c55e',
  },
  {
    id: 'emerald',
    name: 'Emerald',
    description: 'Fresh green',
    colors: {
      primary: '#059669',
      primaryDark: '#047857',
      primaryLight: '#10b981',
    },
    classes: {
      header: 'from-emerald-700 via-emerald-600 to-teal-700',
      headerHover: 'hover:bg-white/10',
      navActive: 'from-emerald-500 to-emerald-600',
      btnPrimary: 'from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800',
      btnPrimaryShadow: 'shadow-emerald-500/25',
      focusRing: 'focus:ring-emerald-500',
      loginBg: 'from-emerald-700 via-teal-800 to-cyan-900',
      spinner: 'text-emerald-600',
      accent: 'text-emerald-600',
      accentBg: 'bg-emerald-50',
      accentBorder: 'border-emerald-200',
    },
    swatch: '#059669',
  },
  {
    id: 'sunset',
    name: 'Sunset',
    description: 'Warm orange',
    colors: {
      primary: '#ea580c',
      primaryDark: '#c2410c',
      primaryLight: '#f97316',
    },
    classes: {
      header: 'from-orange-700 via-orange-600 to-red-700',
      headerHover: 'hover:bg-white/10',
      navActive: 'from-orange-500 to-orange-600',
      btnPrimary: 'from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800',
      btnPrimaryShadow: 'shadow-orange-500/25',
      focusRing: 'focus:ring-orange-500',
      loginBg: 'from-orange-700 via-red-800 to-rose-900',
      spinner: 'text-orange-600',
      accent: 'text-orange-600',
      accentBg: 'bg-orange-50',
      accentBorder: 'border-orange-200',
    },
    swatch: '#ea580c',
  },
  {
    id: 'purple',
    name: 'Purple',
    description: 'Royal purple',
    colors: {
      primary: '#7c3aed',
      primaryDark: '#6d28d9',
      primaryLight: '#8b5cf6',
    },
    classes: {
      header: 'from-purple-700 via-purple-600 to-fuchsia-700',
      headerHover: 'hover:bg-white/10',
      navActive: 'from-purple-500 to-purple-600',
      btnPrimary: 'from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800',
      btnPrimaryShadow: 'shadow-purple-500/25',
      focusRing: 'focus:ring-purple-500',
      loginBg: 'from-purple-800 via-violet-900 to-fuchsia-900',
      spinner: 'text-purple-600',
      accent: 'text-purple-600',
      accentBg: 'bg-purple-50',
      accentBorder: 'border-purple-200',
    },
    swatch: '#7c3aed',
  },
  {
    id: 'rose',
    name: 'Rose',
    description: 'Elegant pink',
    colors: {
      primary: '#e11d48',
      primaryDark: '#be123c',
      primaryLight: '#f43f5e',
    },
    classes: {
      header: 'from-rose-700 via-rose-600 to-pink-700',
      headerHover: 'hover:bg-white/10',
      navActive: 'from-rose-500 to-rose-600',
      btnPrimary: 'from-rose-600 to-rose-700 hover:from-rose-700 hover:to-rose-800',
      btnPrimaryShadow: 'shadow-rose-500/25',
      focusRing: 'focus:ring-rose-500',
      loginBg: 'from-rose-700 via-pink-800 to-red-900',
      spinner: 'text-rose-600',
      accent: 'text-rose-600',
      accentBg: 'bg-rose-50',
      accentBorder: 'border-rose-200',
    },
    swatch: '#e11d48',
  },
  {
    id: 'midnight',
    name: 'Midnight',
    description: 'Dark navy',
    colors: {
      primary: '#1e40af',
      primaryDark: '#1e3a8a',
      primaryLight: '#2563eb',
    },
    classes: {
      header: 'from-slate-900 via-blue-900 to-indigo-950',
      headerHover: 'hover:bg-white/10',
      navActive: 'from-blue-600 to-blue-700',
      btnPrimary: 'from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900',
      btnPrimaryShadow: 'shadow-blue-700/25',
      focusRing: 'focus:ring-blue-500',
      loginBg: 'from-slate-900 via-blue-950 to-indigo-950',
      spinner: 'text-blue-500',
      accent: 'text-blue-500',
      accentBg: 'bg-blue-950/5',
      accentBorder: 'border-blue-200',
    },
    swatch: '#1e40af',
  },
];

const THEME_STORAGE_KEY = 'app_theme';

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved) {
      const found = THEMES.find(t => t.id === saved);
      if (found) return found;
    }
    return THEMES[0]; // Default: Ocean
  });

  // Apply theme attribute to <html> for CSS variable selectors
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme.id);
  }, [theme]);

  const setTheme = useCallback((themeId) => {
    const found = THEMES.find(t => t.id === themeId);
    if (found) {
      setThemeState(found);
      localStorage.setItem(THEME_STORAGE_KEY, themeId);
    }
  }, []);

  const value = {
    theme,
    setTheme,
    themes: THEMES,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
